/**
 * API client with cookie-based authentication and error handling
 */

import { useAuthStore } from '@/stores/authStore'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type QueryParams = Record<string, any>

interface RequestOptions extends RequestInit {
  params?: QueryParams
}

interface ApiError {
  detail: string
  status: number
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private getAuthHeaders(): Record<string, string> {
    const token = useAuthStore.getState().getToken()
    if (token) {
      return { Authorization: `Bearer ${token}` }
    }
    return {}
  }

  private buildUrl(endpoint: string, params?: QueryParams): string {
    const url = new URL(`${this.baseUrl}${endpoint}`, window.location.origin)
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value))
        }
      })
    }
    return url.toString()
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      if (response.status === 401) {
        throw { detail: 'Session expired', status: 401 } as ApiError
      }

      const error: ApiError = {
        detail: 'An error occurred',
        status: response.status,
      }
      try {
        const data = await response.json()
        if (Array.isArray(data.detail)) {
          // FastAPI validation errors come back as a list of objects; joining them
          // keeps the toast readable instead of rendering [object Object]
          error.detail = data.detail
            .map((d: { msg?: string }) => d?.msg)
            .filter(Boolean)
            .join('; ') || error.detail
        } else {
          error.detail = data.detail || error.detail
        }
      } catch {
        // Body was not JSON. nginx returns HTML for a 413, so give a usable message
        // rather than the generic fallback.
        if (response.status === 413) {
          error.detail = 'The upload is too large.'
        }
      }
      throw error
    }
    if (response.status === 204) {
      return undefined as unknown as T
    }
    return response.json()
  }

  async get<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options
    const url = this.buildUrl(endpoint, params)
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
        ...fetchOptions.headers,
      },
      credentials: 'include',
      ...fetchOptions,
    })
    return this.handleResponse<T>(response)
  }

  async post<T>(endpoint: string, data?: unknown, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options
    const url = this.buildUrl(endpoint, params)
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
        ...fetchOptions.headers,
      },
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
      ...fetchOptions,
    })
    return this.handleResponse<T>(response)
  }

  /**
   * POST multipart/form-data.
   *
   * Content-Type is deliberately NOT set: the browser has to generate it itself so
   * it can include the multipart boundary. Setting it by hand produces a request the
   * server cannot parse, which surfaces as "no file received".
   */
  async postForm<T>(
    endpoint: string,
    formData: FormData,
    options: RequestOptions = {}
  ): Promise<T> {
    const { params, headers, ...fetchOptions } = options
    const url = this.buildUrl(endpoint, params)
    const response = await fetch(url, {
      ...fetchOptions,
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        ...headers,
      },
      credentials: 'include',
      body: formData,
    })
    return this.handleResponse<T>(response)
  }

  async put<T>(endpoint: string, data?: unknown, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options
    const url = this.buildUrl(endpoint, params)
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
        ...fetchOptions.headers,
      },
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
      ...fetchOptions,
    })
    return this.handleResponse<T>(response)
  }

  async patch<T>(endpoint: string, data?: unknown, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options
    const url = this.buildUrl(endpoint, params)
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
        ...fetchOptions.headers,
      },
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
      ...fetchOptions,
    })
    return this.handleResponse<T>(response)
  }

  async delete<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options
    const url = this.buildUrl(endpoint, params)
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
        ...fetchOptions.headers,
      },
      credentials: 'include',
      ...fetchOptions,
    })
    return this.handleResponse<T>(response)
  }
}

export const apiClient = new ApiClient(API_BASE_URL)
